import { use, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import useTableFiltering, { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

import GatewayFilter from 'features/gateway/domain/models/gateway-filter';
import { FilterValueType } from 'features/gateway/domain/models/gateway-filter-value';
import GatewayMonitoringFilter from 'features/gateway/domain/models/gateway-monitoring-filter';
import GatewayOverviewRecord from 'features/gateway/domain/models/gateway-overview-record';
import { toGatewaySort } from 'features/gateway/domain/models/gateway-sort';
import { ReadGatewaysQuery } from 'features/gateway/domain/models/read-gateways-query';
import { ReadGatewaysResponse } from 'features/gateway/domain/models/read-gateways-response';
import { useGetMonitorFilters, useReadGateways } from 'features/gateway/domain/queries/gateway-queries';

export interface IGatewayFiltersHook {
  activeFilters: GatewayFilter[];
  searchBarValue: string;
  selectedOrganisationUnits: SelectedOrganisationTreeNode[];
  changeSelectedOrganisationUnits: (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => void;

  submitSearch: (query: string) => void;
  selectOption: (filterValueType: FilterValueType, keys: string[]) => void;
  clearFilters: () => void;

  monitorFilters: GatewayMonitoringFilter[];
  monitorFiltersIsSuccess: boolean;
  getMonitorFilter: (monitoringType: MonitoringType) => GatewayMonitoringFilter;
  loadMonitorFilters: () => void;
  selectMonitorFilter: (monitoringType: MonitoringType, selected: boolean) => void;
  isActiveMonitorFilter: (monitoringType: MonitoringType) => boolean;

  triggerReadGateways: () => void;
  gatewaysResponse: ReadGatewaysResponse | undefined;
  gatewaysResponseIsLoading: boolean;
  gatewaysResponseIsSuccess: boolean;

  table: TableFiltering<GatewayOverviewRecord>;
}

const useGatewayFilters = (): IGatewayFiltersHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const [activeFilters, setActiveFilters] = useState<GatewayFilter[]>([]);

  const table = useTableFiltering<GatewayOverviewRecord>({
    defaultSort: {
      isAscending: true,
      property: 'name',
    },
    sessionStorageKey: 'gateway-sort',
  });

  const defaultReadGatewaysQuery: ReadGatewaysQuery = {
    filters: [],
    pagination: table.currentPagination[0],
    sort: toGatewaySort(table.sortFromSessionStorage),
    searchQuery: '',
  };

  const [readGatewaysQuery, setReadGatewaysQuery] = useState<ReadGatewaysQuery>(defaultReadGatewaysQuery);
  const {
    mutate: triggerReadGateways,
    data: gatewaysResponse,
    error: gatewaysResponseError,
    isError: gatewaysResponseIsError,
    isPending: gatewaysResponseIsLoading,
    isSuccess: gatewaysResponseIsSuccess,
  } = useReadGateways();

  const { refetch: refetchMonitorFilters, data: monitorFiltersData, isSuccess: monitorFiltersIsSuccess } = useGetMonitorFilters();

  const [gatewayFiltersFromSessionStorage, setGatewayFiltersFromSessionStorage] = useSessionStorage<GatewayFilter[]>(
    FilterSessionStorageKeys.GATEWAY_FILTERS,
    [],
  );
  const [selectedOrganisationUnitsFromSessionStorage, setSelectedOrganisationUnitsFromSessionStorage] = useSessionStorage<
    SelectedOrganisationTreeNode[]
  >(FilterSessionStorageKeys.GATEWAY_ORG_UNITS, []);
  const [searchBarValueFromSessionStorage, setSearchBarFromSessionStorage] = useSessionStorage<string>(
    FilterSessionStorageKeys.GATEWAY_SEARCH_BAR,
    '',
  );

  useEffect(() => {
    if (gatewaysResponseIsError && gatewaysResponseError) {
      setErrorMessage(gatewaysResponseError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewaysResponseIsError]);

  const submitSearch = (query: string) => {
    setSearchBarFromSessionStorage(query);
    table.resetPaging();
  };

  const selectOption = (filterValueType: FilterValueType, keys: string[]) => {
    let updatedFiltersList = [...readGatewaysQuery.filters];
    if (updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType) !== -1) {
      updatedFiltersList[updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType)] = {
        filterValueType: filterValueType,
        values: keys,
      };
    } else {
      updatedFiltersList.push({
        filterValueType: filterValueType,
        values: keys,
      });
    }

    updatedFiltersList = updatedFiltersList.filter((x) => x.values.length > 0);

    setGatewayFiltersFromSessionStorage(updatedFiltersList);

    table.resetPaging();
  };

  const changeSelectedOrganisationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) =>
    setSelectedOrganisationUnitsFromSessionStorage(selectedOrganisationUnits);

  const clearFilters = () => {
    setActiveFilters([]);
    setGatewayFiltersFromSessionStorage([]);
    setSelectedOrganisationUnitsFromSessionStorage([]);
    setSearchBarFromSessionStorage('');
    setReadGatewaysQuery(defaultReadGatewaysQuery);

    table.resetPaging();
  };

  const getMonitorFilter = (monitoringType: MonitoringType) => {
    const filter = monitorFiltersData?.find((filter) => filter.monitoringFilterType === monitoringType);
    if (filter) {
      return filter;
    }

    return {
      monitoringFilterType: MonitoringType.All,
      count: monitorFiltersData?.reduce((total, filter) => total + filter.count, 0),
    } as GatewayMonitoringFilter;
  };

  const isActiveMonitorFilter = (monitoringType: MonitoringType) => {
    const filter = activeFilters.find((x) => x.filterValueType === FilterValueType.Monitor);
    if (!filter) {
      return false;
    }

    return filter.values.find((x) => x === monitoringType) !== undefined;
  };

  const loadMonitorFilters = () => {
    refetchMonitorFilters();
  };

  const selectMonitorFilter = (monitoringType: MonitoringType, selected: boolean) => {
    let activeMonitorFilters = [...(activeFilters.find((x) => x.filterValueType === FilterValueType.Monitor)?.values ?? [])];

    if (selected && activeMonitorFilters.find((x) => x === monitoringType) === undefined) {
      activeMonitorFilters = [...activeMonitorFilters, monitoringType];
    } else if (!selected && activeMonitorFilters.find((x) => x === monitoringType) !== undefined) {
      const activeMonitorFilterIndex = activeMonitorFilters.findIndex((x) => x === monitoringType);
      if (activeMonitorFilterIndex >= 0) {
        activeMonitorFilters.splice(activeMonitorFilterIndex, 1);
      }
    }

    selectOption(FilterValueType.Monitor, activeMonitorFilters);
    loadMonitorFilters();
  };

  const readGateways = () => {
    triggerReadGateways(readGatewaysQuery);
  };

  const buildAndSaveQuery = () => {
    setActiveFilters(gatewayFiltersFromSessionStorage);
    const sort = toGatewaySort(table.sortFromSessionStorage);
    if (
      table.currentPagination[0].skip !== readGatewaysQuery?.pagination.skip ||
      table.currentPagination[0].take !== readGatewaysQuery?.pagination.take ||
      sort?.sortType !== readGatewaysQuery.sort?.sortType ||
      sort?.isAscending !== readGatewaysQuery.sort?.isAscending ||
      gatewayFiltersFromSessionStorage !== readGatewaysQuery.filters ||
      searchBarValueFromSessionStorage !== readGatewaysQuery.searchQuery
    ) {
      const newReadGatewaysQuery = {
        pagination: table.currentPagination[0],
        sort,
        filters: gatewayFiltersFromSessionStorage,
        searchQuery: searchBarValueFromSessionStorage,
      };
      setReadGatewaysQuery(newReadGatewaysQuery);
      triggerReadGateways(newReadGatewaysQuery);
    }
  };

  useEffect(() => {
    buildAndSaveQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table.currentPage,
    table.currentRowsPerPage,
    table.sortFromSessionStorage,
    gatewayFiltersFromSessionStorage,
    searchBarValueFromSessionStorage,
  ]);

  return {
    activeFilters,
    searchBarValue: searchBarValueFromSessionStorage,
    selectedOrganisationUnits: selectedOrganisationUnitsFromSessionStorage,
    changeSelectedOrganisationUnits,

    triggerReadGateways: readGateways,
    gatewaysResponse,
    gatewaysResponseIsSuccess,
    gatewaysResponseIsLoading,

    selectOption,
    submitSearch,
    clearFilters,

    monitorFiltersIsSuccess,
    monitorFilters: monitorFiltersData ?? [],
    getMonitorFilter,
    loadMonitorFilters,
    selectMonitorFilter,
    isActiveMonitorFilter,

    table,
  };
};

export default useGatewayFilters;
